import { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import '../styles/Auth.css';

const ForgotPassword = () => {
  const navigate = useNavigate();
  const { startPasswordRecovery, verifyRecoveryCode, resetPassword } = useAuth();
  const [step, setStep] = useState<'start' | 'verify' | 'reset'>('start');
  const [identifier, setIdentifier] = useState('');
  const [otpToken, setOtpToken] = useState('');
  const [resetToken, setResetToken] = useState('');
  const [code, setCode] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);

  const handleSendCode = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!identifier.trim()) {
      setError('Enter your email or phone number');
      return;
    }

    setIsLoading(true);
    setError('');
    setSuccess('');
    try {
      const data = await startPasswordRecovery(identifier);
      setOtpToken(data.otpToken);
      setStep('verify');
      setSuccess(`Recovery code sent${data.maskedDestination ? ` to ${data.maskedDestination}` : ''}`);
    } catch (err: any) {
      setError(err.message || 'Could not send recovery code');
    } finally {
      setIsLoading(false);
    }
  };

  const handleVerifyCode = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!code.trim() || code.length < 6) {
      setError('Enter the 6-digit recovery code');
      return;
    }

    setIsLoading(true);
    setError('');
    setSuccess('');
    try {
      const data = await verifyRecoveryCode(otpToken, code);
      setResetToken(data.resetToken);
      setStep('reset');
      setSuccess('Code verified! Please enter your new password.');
    } catch (err: any) {
      setError(err.message || 'Invalid or expired recovery code');
    } finally {
      setIsLoading(false);
    }
  };

  const handleResetPassword = async (e: React.FormEvent) => {
    e.preventDefault();

    if (newPassword.length < 8) {
      setError('Password must be at least 8 characters');
      return;
    }
    if (newPassword !== confirmPassword) {
      setError('Passwords do not match');
      return;
    }

    setIsLoading(true);
    setError('');
    setSuccess('');
    try {
      await resetPassword(resetToken, newPassword, confirmPassword);
      setSuccess('Password reset successful! Redirecting to sign in...');
      setTimeout(() => navigate('/signin'), 2000);
    } catch (err: any) {
      setError(err.message || 'Could not reset password');
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="auth-page">
      <div className="wave-bg" aria-hidden="true"><span></span></div>

      <div className="page-header">
        <div className="logo-bubble">
          <div className="logo">
            <img className="logo-img" src="/assets/logo2.webp" alt="Olitask" />
          </div>
        </div>
        <h1>Recover password</h1>
        <p className="subtitle">Get back into your Olitask account</p>
      </div>

      <div className="auth-card">
        <div className={`error-banner ${error ? 'show' : ''}`}>
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#991b1b" strokeWidth="2">
            <circle cx="12" cy="12" r="10"/>
            <line x1="12" y1="8" x2="12" y2="12"/>
            <line x1="12" y1="16" x2="12.01" y2="16"/>
          </svg>
          <span>{error}</span>
        </div>

        <div className={`error-banner ${success ? 'show' : ''}`} style={{ background: '#ecfdf3', borderColor: '#bbf7d0', color: '#166534' }}>
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#166534" strokeWidth="2">
            <path d="M20 6L9 17l-5-5" stroke="currentColor" strokeWidth="2" fill="none"/>
          </svg>
          <span>{success}</span>
        </div>

        {/* Step 1: Enter email/phone */}
        {step === 'start' && (
          <form onSubmit={handleSendCode}>
            <p className="hint">Enter the email address or phone number on your account. We will send you a recovery code.</p>
            <div className="field">
              <label>Email or phone</label>
              <input
                type="text"
                placeholder="you@example.com or +1234567890"
                value={identifier}
                onChange={(e) => setIdentifier(e.target.value)}
                disabled={isLoading}
                autoFocus
              />
            </div>
            <div className="btn-row">
              <Link to="/signin" className="btn btn-cancel">Cancel</Link>
              <button type="submit" className="btn btn-login" disabled={isLoading}>
                {isLoading ? 'Sending...' : 'Send Code'}
              </button>
            </div>
          </form>
        )}

        {/* Step 2: Enter verification code */}
        {step === 'verify' && (
          <form onSubmit={handleVerifyCode}>
            <p className="hint" id="codeHint">Enter the 6-digit recovery code we sent to your account.</p>
            <div className="field">
              <label>Recovery code</label>
              <input
                type="text"
                inputMode="numeric"
                maxLength={6}
                placeholder="6-digit code"
                value={code}
                onChange={(e) => setCode(e.target.value.replace(/\D/g, ''))}
                disabled={isLoading}
                autoFocus
              />
            </div>
            <div className="btn-row">
              <button type="button" className="btn btn-cancel" onClick={() => setStep('start')}>Back</button>
              <button type="submit" className="btn btn-login" disabled={isLoading}>
                {isLoading ? 'Verifying...' : 'Verify Code'}
              </button>
            </div>
            <p className="footer-links" style={{ marginTop: '16px' }}>
              Didn't receive a code? <button type="button" onClick={() => setStep('start')} style={{ background: 'none', border: 'none', color: '#1e9e45', cursor: 'pointer', fontWeight: 'bold' }}>Try again</button>
            </p>
          </form>
        )}

        {/* Step 3: Reset password */}
        {step === 'reset' && (
          <form onSubmit={handleResetPassword}>
            <p className="hint">Create a new password for your account.</p>
            <div className="field">
              <label>New password</label>
              <div className="pw-wrap">
                <input
                  type={showPassword ? 'text' : 'password'}
                  placeholder="At least 8 characters"
                  value={newPassword}
                  onChange={(e) => setNewPassword(e.target.value)}
                  disabled={isLoading}
                  autoFocus
                />
                <button
                  type="button"
                  className="pw-toggle"
                  onClick={() => setShowPassword(!showPassword)}
                >
                  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"/>
                    <circle cx="12" cy="12" r="3"/>
                  </svg>
                </button>
              </div>
            </div>
            <div className="field">
              <label>Confirm password</label>
              <div className="pw-wrap">
                <input
                  type={showConfirmPassword ? 'text' : 'password'}
                  placeholder="Repeat new password"
                  value={confirmPassword}
                  onChange={(e) => setConfirmPassword(e.target.value)}
                  disabled={isLoading}
                />
                <button
                  type="button"
                  className="pw-toggle"
                  onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                >
                  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"/>
                    <circle cx="12" cy="12" r="3"/>
                  </svg>
                </button>
              </div>
            </div>
            <div className="btn-row">
              <button type="button" className="btn btn-cancel" onClick={() => setStep('verify')}>Back</button>
              <button type="submit" className="btn btn-login" disabled={isLoading}>
                {isLoading ? 'Resetting...' : 'Reset Password'}
              </button>
            </div>
          </form>
        )}

        <div className="footer-links" style={{ marginTop: '20px' }}>
          Remembered it? <Link to="/signin">Sign In</Link>
        </div>
      </div>
    </div>
  );
};

export default ForgotPassword;